import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';

import styles from './Swiper.web.module.css';

import { Box, Image } from '@betty/moonlight';
import { Pressable } from 'react-native';

import { Image as RnImage } from 'react-native';

import type { EmblaCarouselType } from 'embla-carousel';
import { RequiredSwiperItemProps, SwiperProps } from './types';
import ArrowNavigation from './components/ArrowNavigation';
import DotNavigation from './components/DotNavigation/DotNavigation';

function Swiper<SwiperItem extends RequiredSwiperItemProps>({
  items,
  autoplay = false,
  pagination = false,
  onClick,
  imageContentFit = 'contain',
  renderOverlayItem,
  arrows = false,
  loop = true,
  peek = false,
  slidesPerView = 1,
  itemsGap,
  dragFree,
  slidesToScroll,
  itemWidth,
  itemRadius,
  ...rest
}: SwiperProps<SwiperItem>) {
  const autoplayPlugin = useMemo(
    () => Autoplay({ stopOnInteraction: false }),
    [],
  );

  const plugins = useMemo(() => {
    return autoplay ? [autoplayPlugin] : [];
  }, [autoplay, autoplayPlugin]);

  const shouldPeek = useMemo(() => {
    return peek && slidesPerView < items.length;
  }, [items.length, peek, slidesPerView]);

  const shouldNavigate = useMemo(() => {
    return items.length > 1 && slidesPerView < items.length;
  }, [items.length, slidesPerView]);

  const slideWidth = useMemo(() => {
    return slidesPerView
      ? `${(shouldPeek ? (slidesPerView > 1 ? 85 : 70) : 100 - (itemsGap ? slidesPerView : 0)) / slidesPerView}%`
      : '100%';
  }, [itemsGap, shouldPeek, slidesPerView]);

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop,
      align: slidesPerView === 1 ? 'center' : 'start',
      containScroll: shouldPeek || dragFree ? 'trimSnaps' : undefined,
      dragFree: dragFree,
      slidesToScroll: slidesToScroll,
    },

    plugins,
  );

  const [aspectRatio, setAspectRatio] = useState<number>();

  useEffect(() => {
    if (items[0]?.image) {
      RnImage.getSize(items[0]?.image, (width, height) => {
        setAspectRatio(width / height);
      });
    }
  }, [items]);

  useEffect(() => {
    if (shouldNavigate) {
      emblaApi?.reInit();
      emblaApi?.scrollTo(0);
    } else {
      emblaApi?.reInit();
      emblaApi?.scrollTo(0);
      emblaApi?.destroy();
    }
  }, [emblaApi, items.length, shouldNavigate, slidesPerView]);

  const onNavButtonClick = useCallback((emblaApi: EmblaCarouselType) => {
    const autoplay = emblaApi?.plugins()?.autoplay;
    if (!autoplay) return;

    const resetOrStop =
      autoplay.options.stopOnInteraction === false
        ? autoplay.reset
        : autoplay.stop;

    resetOrStop();
  }, []);

  return (
    <Box
      key={shouldNavigate ? 'enabled' : 'disabled'}
      overflow={peek ? 'visible' : 'hidden'}
      position="relative"
      width="100%"
      height="100%"
      {...rest}
    >
      <div className={styles.wrapper} ref={emblaRef}>
        <Box
          flexDirection="row"
          height="100%"
          position="relative"
          gap={itemsGap}
          justifyContent={shouldNavigate ? 'space-between' : 'center'}
        >
          {items.map((item, index) => (
            <Box
              height="100%"
              justifyContent="center"
              alignItems="center"
              overflow="hidden"
              key={index}
              borderRadius={itemRadius ?? 'radius-0'}
              {...(!slidesToScroll
                ? {
                    flex: `0 0 ${imageContentFit === 'contain' ? 'auto' : slideWidth}`,
                  }
                : { width: itemWidth })}
            >
              <Pressable
                onPress={() => onClick?.(index)}
                style={{ width: '100%', height: '100%', display: 'flex', cursor: onClick ? 'pointer' : 'default' }}
              >
                {item?.image !== '' ? (
                  <Image
                    source={item?.image}
                    contentFit={imageContentFit}
                    aspectRatio={
                      imageContentFit == 'contain' ? aspectRatio : undefined
                    }
                    width="100%"
                    height="100%"
                    display="block"
                    objectPosition="center center"
                  />
                ) : null}
                {renderOverlayItem && renderOverlayItem(item)}
              </Pressable>
            </Box>
          ))}
        </Box>
      </div>

      {shouldNavigate ? (
        <>
          {arrows ? (
            <ArrowNavigation
              emblaApi={emblaApi}
              onNavButtonClick={onNavButtonClick}
            />
          ) : null}

          {pagination ? <DotNavigation emblaApi={emblaApi} /> : null}
        </>
      ) : null}
    </Box>
  );
}

export default memo(Swiper);
